import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Nav,
  NavItem,
  NavLink as NavLinkBase,
  TabContent,
  TabPane,
  Table,
  Row,
  Col,
  Spinner,
} from 'reactstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClientIcon } from 'assets/icon-consulting.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { LabelIcon } from 'shared/components/Labels';
import { Button, ButtonIcon, ButtonLabelIcon } from 'shared/components/Buttons';
import BackgroundBox from 'shared/components/BackgroundBox';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';

import useApi from 'hooks/useApi';

import { SEARCH_ALL_SITE_OPTION, SEARCH_CLIENT_OPTION } from 'redux/reducers/topBarReducer';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, emptyString } from 'shared/utils';
import { colors, fonts, fontSizes, device } from 'shared/defaultTheme';
import { setDeleteClientFromHome } from 'redux/actions/clientActions';

import { TdNameClientCard } from '../styles';

const HeaderCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media ${device.sm} {
    flex-direction: column;
    margin-bottom: 15px;
    margin-top: 5px;
  }
  @media ${device.md} {
    margin-bottom: 45px;
    margin-top: 0;
  }
  @media ${device.lg} {
    margin-bottom: 45px;
    margin-top: 0;
  }

  @media ${device.xl} {
    margin-bottom: 45px;
    margin-top: 0;
  }

  svg {
    height: 20px;
    width: 17px;
  }
`;

const Tbody = styled.tbody`
  font-family: ${fonts.medium};
  font-size: ${fontSizes.sm}px;
  .link-name-client {
    cursor: pointer;
    &:hover {
      color: ${colors.suvaGrey};
    }
  }
`;

const NavHome = styled(Nav)`
  border-bottom: 1px solid ${({ color }) => color};
`;

const NavLink = styled(NavLinkBase)`
  border-color: ${({ border }) => border} !important;
  color: ${({ color }) => color};
  background-color: ${({ background }) => background} !important;
  font-family: ${fonts.default};
  font-weight: bold;
  cursor: pointer;
  &:hover {
    color: ${({ color }) => color};
  }
  @media ${device.sm} {
    font-size: 12px;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TdActions = styled.td`
  text-align: end;
  width: 10% !important;
`;

const ClientSectionCard = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const auth = useSelector((state) => state.auth);
  const { searchOption, searchValue } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const [activeTab, setActiveTab] = useState('1');
  const [lastCreatedClients, setLastCreatedClients] = useState([]);
  const [lastUpdatedClients, setLastUpdatedClients] = useState([]);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [selectedClientDelete, setSelectedClientDelete] = useState();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [{ loading: loadingLastCratedClients }, getLastCreatedClients] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Client',
            field: 'Created',
            asc: false,
          },
        ],
        reduce: {
          limit: 8,
          skip: 0,
        },
      },
    },
    { manual: true }
  );
  const [{ loading: loadingLastUpdatedClients }, getLastUpdatedClients] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Client',
            field: 'Modified',
            asc: false,
          },
        ],
        reduce: {
          limit: 8,
          skip: 0,
        },
      },
    },
    { manual: true }
  );
  const [{ loading: loadingDelete }, deleteClient] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );
  const { mainColor } = theme;
  const fetchDataClients = async (searchClient = undefined) => {
    const {
      data: { data: lastCreatedData, errors: errorsGettingLastCreated },
    } = await getLastCreatedClients(
      searchClient
        ? {
            data: {
              filters: [
                {
                  model: 'Client',
                  field: 'ClientId',
                  filter: [searchClient],
                },
                {
                  model: 'Client',
                  field: 'Created',
                  asc: false,
                },
              ],
              reduce: {
                limit: 5,
                skip: 0,
              },
            },
          }
        : undefined
    );
    if (errorsGettingLastCreated.length) {
      return;
    }
    setLastCreatedClients(lastCreatedData);
    const {
      data: { data: lastUpdatedData, errors: errorsGettingLastUpdated },
    } = await getLastUpdatedClients(
      searchClient
        ? {
            data: {
              filters: [
                {
                  model: 'Client',
                  field: 'ClientId',
                  filter: [searchClient],
                },
                {
                  model: 'Client',
                  field: 'Modified',
                  asc: false,
                },
              ],
              reduce: {
                limit: 5,
                skip: 0,
              },
            },
          }
        : undefined
    );
    if (errorsGettingLastUpdated.length) {
      return;
    }
    setLastUpdatedClients(lastUpdatedData);
  };

  const onRefreshData = () => {
    if (SEARCH_CLIENT_OPTION === searchOption && !emptyString(searchValue)) {
      fetchDataClients(searchValue);
    }
    if (SEARCH_CLIENT_OPTION === searchOption && emptyString(searchValue)) {
      fetchDataClients();
    }
    if (SEARCH_ALL_SITE_OPTION === searchOption) {
      fetchDataClients();
    }
  };

  useEffect(() => {
    onRefreshData();
  }, [searchOption, searchValue]);

  const showAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setOpenAlert(true);
  };

  const closeAlert = () => {
    setSelectedClientDelete(undefined);
    setAlertType('');
    setAlertMessage('');
    setOpenAlert(false);
    fetchDataClients();
    dispatch(setDeleteClientFromHome(true));
  };

  const onAcceptDelete = async () => {
    if (selectedClientDelete != null) {
      dispatch(setDeleteClientFromHome(false));
      try {
        const {
          data: { errors },
        } = await deleteClient({
          url: `${window.API_V1}/cli/clients/${auth.cusNavId}/${selectedClientDelete.clientId}?login=${auth.mail}`,
        });
        if (errors.length) {
          setOpenConfirmDelete(false);
          showAlert(ALERT_TYPE_ERROR, t('general_delete_error'));
          return;
        }
        setOpenConfirmDelete(false);
        showAlert(
          ALERT_TYPE_SUCCESS,
          t('client_delete_success').replace('{clientName}', selectedClientDelete.clientName)
        );
      } catch (ex) {
        setOpenConfirmDelete(false);
        showAlert(ALERT_TYPE_ERROR, t('general_delete_error'));
      }
    }
  };

  const onCancelDelete = () => {
    setSelectedClientDelete(undefined);
    setOpenConfirmDelete(false);
  };

  const onDelete = (client) => {
    setSelectedClientDelete(client);
    setOpenConfirmDelete(true);
  };
  if (loadingLastCratedClients || loadingLastUpdatedClients || loadingDelete) {
    return (
      <BackgroundBox className="ml-background-dashboard background-box__flex-home">
        <HeaderCardWrapper>
          <LabelIcon
            label={t('home_client_section_label')}
            Icon={() => <ClientIcon fill={colors.white} />}
            color={mainColor}
            className="text-uppercase ml-1 label-icon"
          />
          <div className="d-flex w-100 justify-content-end">
            <ButtonLabelIcon
              id="add-icon-client"
              text={t('general_button_new')}
              type="button"
              Icon={() => <AddIcon fill={colors.black} />}
              className="button-icon mr-1"
            />
            <ButtonLabelIcon
              id="refresh-icon-client"
              text={t('general_button_refresh')}
              type="button"
              Icon={() => <RefreshIcon fill={colors.black} />}
              className="button-icon"
            />
          </div>
        </HeaderCardWrapper>
        <SpinnerWrapper>
          <Spinner color={mainColor} />
        </SpinnerWrapper>
      </BackgroundBox>
    );
  }
  return (
    <BackgroundBox className="ml-background-dashboard background-box__flex-home">
      <HeaderCardWrapper>
        <div onClick={() => history.push('/clients')} aria-hidden="true">
          <LabelIcon
            label={t('home_client_section_label')}
            Icon={() => <ClientIcon fill={colors.white} />}
            color={mainColor}
            className="text-uppercase ml-1 label-icon"
            isDashboard
          />
        </div>

        <div className="d-flex w-100 justify-content-end">
          <ButtonLabelIcon
            id="add-icon-client"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={() => history.push('clients/new')}
          />
          <ButtonLabelIcon
            id="refresh-icon-client"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon"
            onClick={onRefreshData}
          />
        </div>
      </HeaderCardWrapper>
      <NavHome tabs color={mainColor}>
        <NavItem>
          <NavLink
            className="text-uppercase ml-1"
            border={mainColor}
            color={activeTab === '1' ? colors.white : mainColor}
            background={activeTab === '1' ? mainColor : colors.white}
            onClick={() => setActiveTab('1')}
          >
            {t('home_client_section_tab_last_movement')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className="text-uppercase"
            border={mainColor}
            color={activeTab === '2' ? colors.white : mainColor}
            background={activeTab === '2' ? mainColor : colors.white}
            onClick={() => setActiveTab('2')}
          >
            {t('home_client_section_tab_last_new_client')}
          </NavLink>
        </NavItem>
      </NavHome>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="1">
          <Row>
            <Col sm="12">
              <Table className="home__document-table mt-4">
                <thead>
                  <tr />
                </thead>
                <Tbody>
                  {lastUpdatedClients.map((client, index) => (
                    <React.Fragment key={client.clientId}>
                      <tr>
                        <TdNameClientCard>
                          <div
                            aria-hidden="true"
                            onClick={() => history.push(`clients/update/${client.clientId}`)}
                            className="link-name-client"
                          >
                            <p>{client.clientName}</p>
                          </div>
                        </TdNameClientCard>
                        <TdActions>
                          <div className="d-flex">
                            <ButtonIcon
                              id={`edit-icon-client-${index}`}
                              title={t('clients_edit_client')}
                              type="button"
                              Icon={() => <EditIcon fill={colors.suvaGrey} />}
                              className="button-icon"
                              onClick={() => history.push(`clients/update/${client.clientId}`)}
                            />
                            <ButtonIcon
                              id={`trash-icon-client-${index}`}
                              title={t('general_delete')}
                              type="button"
                              Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                              className="button-icon mr-n3 ml-2"
                              onClick={() => onDelete(client)}
                            />
                          </div>
                        </TdActions>
                      </tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
              {!lastUpdatedClients.length && (
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <ClientIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
                  <span>{t('client_not_found')}</span>
                </div>
              )}
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="2">
          <Row>
            <Col sm="12">
              <Table className="home__document-table mt-4">
                <thead>
                  <tr />
                </thead>
                <Tbody>
                  {lastCreatedClients.map((client, index) => (
                    <React.Fragment key={client.clientId}>
                      <tr>
                        <TdNameClientCard>
                          <div
                            aria-hidden="true"
                            onClick={() => history.push(`clients/update/${client.clientId}`)}
                            className="link-name-client"
                          >
                            <p>{client.clientName}</p>
                          </div>
                        </TdNameClientCard>
                        <TdActions>
                          <div className="d-flex">
                            <ButtonIcon
                              id={`edit-icon-client-${index}`}
                              title={t('clients_edit_client')}
                              type="button"
                              Icon={() => <EditIcon fill={colors.suvaGrey} />}
                              className="button-icon"
                              onClick={() => history.push(`clients/update/${client.clientId}`)}
                            />
                            <ButtonIcon
                              id={`trash-icon-client-${index}`}
                              title={t('general_delete')}
                              type="button"
                              Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                              className="button-icon mr-n3 ml-2"
                              onClick={() => onDelete(client)}
                            />
                          </div>
                        </TdActions>
                      </tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
              {!lastCreatedClients.length && (
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <ClientIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
                  <span>{t('client_not_found')}</span>
                </div>
              )}
            </Col>
          </Row>
        </TabPane>
      </TabContent>
      {lastCreatedClients.length > 0 && lastUpdatedClients.length > 0 && (
        <div className="action-btn">
          <Button
            className="home__btn text-uppercase"
            type="submit"
            onClick={() => history.push('/clients')}
            label={t('general_see_all')}
            color="primary"
          />
        </div>
      )}
      <ConfirmModal
        isOpen={openConfirmDelete}
        toggle={onCancelDelete}
        confirmMessage={t('client_delete_confirmation').replace(
          '{clientName}',
          selectedClientDelete?.clientName
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelDelete}
        onOk={onAcceptDelete}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={closeAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={closeAlert}
      />
    </BackgroundBox>
  );
};

export default ClientSectionCard;
